import { useSyncExternalStore, type CSSProperties } from 'react';

interface EnvConfig {
  label: string;
  bg: string;
  fg: string;
}

const KNOWN_NON_PROD: Record<string, EnvConfig> = {
  acceptance: { label: 'ACCEPTANCE', bg: '#ec4899', fg: '#ffffff' },
  development: { label: 'DEVELOPMENT', bg: '#84cc16', fg: '#000000' },
  'ai-development': { label: 'AI DEV', bg: '#a855f7', fg: '#ffffff' },
  local: { label: 'LOCAL', bg: '#06b6d4', fg: '#000000' },
};

const RAW = (import.meta.env.VITE_APP_ENV ?? '').toString().trim().toLowerCase();
const CONFIG: EnvConfig | null = Object.prototype.hasOwnProperty.call(KNOWN_NON_PROD, RAW)
  ? KNOWN_NON_PROD[RAW]
  : null;

interface UserInfo {
  tenantName?: string;
  userEmail?: string;
  roles?: string[];
}

const userInfoStore = (() => {
  let value: UserInfo = {};
  const listeners = new Set<() => void>();
  return {
    getSnapshot: (): UserInfo => value,
    set: (info: UserInfo): void => {
      value = { ...info };
      listeners.forEach((l) => l());
    },
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
})();

export const setEnvBarUserInfo = userInfoStore.set;

export interface EnvIndicatorBarProps {
  tenantName?: string;
  userEmail?: string;
  roles?: string[];
}

export const EnvIndicatorBar = ({ tenantName, userEmail, roles }: EnvIndicatorBarProps) => {
  const storeValue = useSyncExternalStore(
    userInfoStore.subscribe,
    userInfoStore.getSnapshot,
    userInfoStore.getSnapshot, // SSR snapshot — safe since store is module-local
  );

  if (!CONFIG) return null;

  const effectiveTenant = tenantName ?? storeValue.tenantName;
  const effectiveEmail = userEmail ?? storeValue.userEmail;
  const effectiveRoles = roles ?? storeValue.roles;

  const containerStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 1001,
    width: '100%',
    padding: '6px 16px',
    backgroundColor: CONFIG.bg,
    backgroundImage: `repeating-linear-gradient(135deg, ${CONFIG.bg} 0 12px, rgba(0,0,0,0.18) 12px 24px)`,
    color: CONFIG.fg,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxSizing: 'border-box',
    letterSpacing: '0.04em',
  };

  return (
    <div role="status" aria-live="polite" style={containerStyle}>
      <strong>{CONFIG.label}</strong>
      {effectiveTenant ? <span>· {effectiveTenant}</span> : null}
      {effectiveEmail ? <span>· {effectiveEmail}</span> : null}
      {effectiveRoles && effectiveRoles.length > 0 ? <span>· {effectiveRoles.join(', ')}</span> : null}
    </div>
  );
};
